/**
 * Browser-local auth + ontology-context storage for the ontology API. Pulled out
 * so `ontology-api-base.ts` can wire the headers
 * into `ontologyBaseQuery` without dragging the whole API slice along.
 *
 * All values live in `localStorage` keyed on the constants below; on
 * non-browser runtimes (SSR / unit tests) every accessor short-
 * circuits to a safe empty value so callers can use them
 * unconditionally.
 */

const STORAGE_KEY_BRANCH = 'ontology-branch'
const STORAGE_KEY_SITE = 'ontology-site'
const STORAGE_KEY_API_KEY = 'ontology-api-key'

export const ONTOLOGY_ALL_SITES = '*'

export function getOntologyBranchHeaderValue() {
  if (typeof window === 'undefined' || !('localStorage' in window)) {
    return undefined
  }

  const branch = window.localStorage.getItem(STORAGE_KEY_BRANCH)?.trim()
  return branch || undefined
}

export function setOntologyBranch(branch: string | undefined) {
  if (typeof window === 'undefined' || !('localStorage' in window)) {
    return
  }
  if (branch) {
    window.localStorage.setItem(STORAGE_KEY_BRANCH, branch)
  } else {
    window.localStorage.removeItem(STORAGE_KEY_BRANCH)
  }
}

export function getOntologySiteHeaderValue() {
  if (typeof window === 'undefined' || !('localStorage' in window)) {
    return undefined
  }

  const site = window.localStorage.getItem(STORAGE_KEY_SITE)?.trim()
  return site || ONTOLOGY_ALL_SITES
}

export function setOntologySite(site: string | undefined) {
  if (typeof window === 'undefined' || !('localStorage' in window)) {
    return
  }
  if (site && site !== ONTOLOGY_ALL_SITES) {
    window.localStorage.setItem(STORAGE_KEY_SITE, site)
  } else {
    window.localStorage.removeItem(STORAGE_KEY_SITE)
  }
}

export function setApiKey(key: string) {
  if (typeof window !== 'undefined' && 'localStorage' in window) {
    if (key) {
      window.localStorage.setItem(STORAGE_KEY_API_KEY, key)
    } else {
      window.localStorage.removeItem(STORAGE_KEY_API_KEY)
    }
  }
}

export function getApiKey(): string {
  if (typeof window === 'undefined' || !('localStorage' in window)) {
    return ''
  }
  return window.localStorage.getItem(STORAGE_KEY_API_KEY)?.trim() ?? ''
}
